import React, { useMemo } from 'react';
import styled from 'styled-components';
import type { WorkflowDocument, WorkflowNode } from '../../store/api';
import { layoutGraph } from './graphLayout';

const NODE_WIDTH = 168;
const NODE_HEIGHT = 58;
const GAP_X = 56;
const GAP_Y = 22;

const Scroll = styled.div`
  overflow: auto;
  padding: 8px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--bg-surface);
  /* A list reads better than a tiny drawing on a phone. */
  @media (max-width: 768px) {
    display: none;
  }
`;

const Canvas = styled.div`
  position: relative;
`;

const Lines = styled.svg`
  position: absolute;
  inset: 0;
  pointer-events: none;
`;

const statusColour: Record<string, string> = {
  failed: 'var(--danger, #c0392b)',
  interrupted: 'var(--danger, #c0392b)',
  waiting: 'var(--warning, #b7791f)',
  running: 'var(--accent)',
  succeeded: 'var(--success, #2f855a)',
};

const Box = styled.button<{ $selected: boolean; $status?: string; $dim: boolean }>`
  position: absolute;
  width: ${NODE_WIDTH}px;
  height: ${NODE_HEIGHT}px;
  text-align: left;
  padding: 8px 10px;
  border-radius: 8px;
  cursor: pointer;
  background: var(--bg-elevated);
  color: var(--text-primary);
  opacity: ${(props) => (props.$dim ? 0.45 : 1)};
  border: ${(props) => (props.$selected ? 2 : 1)}px solid
    ${(props) =>
      props.$selected
        ? 'var(--accent)'
        : (props.$status && statusColour[props.$status]) || 'var(--border)'};
  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }
`;

const BoxTitle = styled.div`
  font-size: 0.8125rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const BoxSub = styled.div`
  font-size: 0.75rem;
  color: var(--text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PhoneList = styled.ol`
  display: none;
  list-style: none;
  margin: 0;
  padding: 0;
  @media (max-width: 768px) {
    display: block;
  }
  li {
    border-bottom: 1px solid var(--border);
  }
  button {
    width: 100%;
    text-align: left;
    padding: 10px 4px;
    background: none;
    border: 0;
    color: var(--text-primary);
    cursor: pointer;
  }
  small {
    display: block;
    color: var(--text-muted);
  }
`;

export interface StepPaint {
  status: string;
  passes: number;
}

function subtitle(node: WorkflowNode, paint?: StepPaint): string {
  const passes = paint && paint.passes > 1 ? ` · ${paint.passes} passes` : '';
  if (node.type === 'trigger') return `Start${passes}`;
  if (node.type === 'end') return 'End';
  if (node.type === 'decision') return `Decision${passes}`;
  if (node.wait?.kind === 'approval') return `Needs approval${passes}`;
  return `${node.agent?.name || 'Needs an agent'}${passes}`;
}

interface Props {
  document: WorkflowDocument;
  selected?: string | null;
  onSelect?: (id: string) => void;
  /** A run paints what happened. Without one, this draws the definition. */
  paint?: Record<string, StepPaint>;
}

const WorkflowGraph: React.FC<Props> = ({ document, selected, onSelect, paint }) => {
  const layout = useMemo(() => layoutGraph(document), [document]);
  const position = useMemo(() => {
    const result = new Map<string, { x: number; y: number }>();
    layout.forEach((node) => {
      result.set(node.id, {
        x: node.rank * (NODE_WIDTH + GAP_X),
        y: node.row * (NODE_HEIGHT + GAP_Y),
      });
    });
    return result;
  }, [layout]);
  const width = (Math.max(0, ...layout.map((node) => node.rank)) + 1) * (NODE_WIDTH + GAP_X);
  const height = (Math.max(0, ...layout.map((node) => node.row)) + 1) * (NODE_HEIGHT + GAP_Y) + 40;

  const edges = document.edges.flatMap((edge, index) => {
    const from = position.get(edge.from);
    const to = position.get(edge.to);
    if (!from || !to) return [];
    const startX = from.x + NODE_WIDTH;
    const startY = from.y + NODE_HEIGHT / 2;
    const endY = to.y + NODE_HEIGHT / 2;
    const path = edge.loop
      ? `M ${from.x + NODE_WIDTH / 2} ${from.y + NODE_HEIGHT} C ${from.x} ${from.y + NODE_HEIGHT + 34}, ${to.x + NODE_WIDTH} ${to.y + NODE_HEIGHT + 34}, ${to.x + NODE_WIDTH / 2} ${to.y + NODE_HEIGHT}`
      : `M ${startX} ${startY} C ${startX + 28} ${startY}, ${to.x - 28} ${endY}, ${to.x} ${endY}`;
    const labelX = edge.loop ? (from.x + to.x + NODE_WIDTH) / 2 : (startX + to.x) / 2;
    const labelY = edge.loop ? from.y + NODE_HEIGHT + 30 : (startY + endY) / 2 - 4;
    return [
      <g key={`${edge.from}-${edge.to}-${index}`}>
        <path
          d={path}
          fill="none"
          stroke="var(--text-muted)"
          strokeWidth="1.25"
          strokeDasharray={edge.loop ? '4 3' : undefined}
          markerEnd="url(#workflow-arrow)"
        />
        {edge.when && (
          <text x={labelX} y={labelY} fontSize="11" textAnchor="middle" fill="var(--text-secondary)">
            {edge.when}
            {edge.loop && edge.max_passes ? ` (max ${edge.max_passes})` : ''}
          </text>
        )}
      </g>,
    ];
  });

  return (
    <>
      <Scroll>
        <Canvas style={{ width, height }}>
          <Lines width={width} height={height} aria-hidden="true">
            <defs>
              <marker id="workflow-arrow" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto">
                <path d="M0,0 L8,4 L0,8 z" fill="var(--text-muted)" />
              </marker>
            </defs>
            {edges}
          </Lines>
          {document.nodes.map((node) => {
            const at = position.get(node.id)!;
            const stepPaint = paint?.[node.id];
            return (
              <Box
                key={node.id}
                type="button"
                style={{ left: at.x, top: at.y }}
                $selected={selected === node.id}
                $status={stepPaint?.status}
                $dim={Boolean(paint) && stepPaint?.status === 'not_taken'}
                onClick={() => onSelect?.(node.id)}
              >
                <BoxTitle>{node.type === 'trigger' ? 'Trigger' : node.title || node.id}</BoxTitle>
                <BoxSub>{subtitle(node, stepPaint)}</BoxSub>
              </Box>
            );
          })}
        </Canvas>
      </Scroll>
      <PhoneList>
        {document.nodes.map((node) => (
          <li key={node.id}>
            <button type="button" onClick={() => onSelect?.(node.id)}>
              {node.type === 'trigger' ? 'Trigger' : node.title || node.id}
              <small>{subtitle(node, paint?.[node.id])}</small>
              {document.edges
                .filter((edge) => edge.from === node.id && edge.when)
                .map((edge) => (
                  <small key={edge.to}>
                    {edge.when} → {edge.to}
                  </small>
                ))}
            </button>
          </li>
        ))}
      </PhoneList>
    </>
  );
};

export default WorkflowGraph;
